import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { inventoryApi } from "../api/inventory";
import { stashApi } from "../api/stash";
import { useGameStore } from "../store/useGameStore";
import { isMountCardItemId } from "../utils/assets";
import { useMountsStore } from "../store/useMountsStore";
import { useBlacksmithStore } from "../store/useBlacksmithStore";
import { ItemDetailModal } from "../components/ItemDetailModal";
import { ConsumableDetailModal } from "../components/ConsumableDetailModal";
import { GemDetailModal } from "../components/GemDetailModal";
import { formatGemLabel, gemTypeColor, gemTypeSurface } from "../components/GemPickerModal";
import {
  getConsumableIconPath,
  getGemIconPath,
  getItemIconPath,
  getLegendaryStoneIconPath,
  getShardIconPath,
  GEM_ICON_FALLBACK,
  LEGENDARY_STONE_ICON_FALLBACK,
  RARITY_COLOR,
  SHARD_ICON_FALLBACK,
  USABLE_STASH_ITEMS,
} from "../utils/assets";
import { AdminAssetPath } from "../components/AdminAssetPath";
import { AssetIcon } from "../components/AssetIcon";
import type { ConsumableItem, GemStack, InventoryOverview, PlayerItem } from "../types";

export function InventoryScreen() {
  const { hero, balance, refreshHero, refreshBalance, setScreen, pushInfoMessages } = useGameStore();
  const { sellGem, setTab: setBlacksmithTab, config: blacksmithConfig } = useBlacksmithStore();
  const [overview, setOverview] = useState<InventoryOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PlayerItem | null>(null);
  const [selectedConsumable, setSelectedConsumable] = useState<ConsumableItem | null>(null);
  const [selectedGem, setSelectedGem] = useState<GemStack | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    void useBlacksmithStore.getState().refresh();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryApi.getOverview();
      if (!data || !Array.isArray(data.items) || !Array.isArray(data.consumables)) {
        throw new Error("Invalid inventory response");
      }
      setOverview(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load inventory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runUpgrade = async (itemId: number, luckLevel: number, keepSelected = false) => {
    setActionLoading(true);
    setSuccess(null);
    setError(null);
    try {
      const res = await inventoryApi.upgrade(itemId, luckLevel);
      setOverview(res.inventory);
      setSuccess(res.message);
      await Promise.all([refreshHero(), refreshBalance()]);
      if (keepSelected) {
        const updated = res.inventory.items.find((i) => i.id === itemId);
        if (updated) setSelected(updated);
      } else {
        setSelected(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upgrade failed");
    } finally {
      setActionLoading(false);
    }
  };

  const runAction = async (fn: () => Promise<InventoryOverview>, keepSelected = false) => {
    setActionLoading(true);
    setSuccess(null);
    try {
      const data = await fn();
      setOverview(data);
      await Promise.all([refreshHero(), refreshBalance()]);
      if (keepSelected && selected) {
        const updated = data.items.find((i) => i.id === selected.id);
        if (updated) setSelected(updated);
      } else {
        setSelected(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const useConsumable = async (item: ConsumableItem, quantity: number) => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await stashApi.use(item.item_id, quantity);
      const data = await inventoryApi.getOverview();
      setOverview(data);
      await Promise.all([refreshHero(), refreshBalance()]);
      if (isMountCardItemId(item.item_id)) {
        await useMountsStore.getState().refresh();
      }
      setSelectedConsumable(null);
      const msg = result.message ?? `${item.name} used`;
      setSuccess(msg);
      if (result.message) pushInfoMessages([result.message]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to use item");
    } finally {
      setActionLoading(false);
    }
  };

  if (!hero) return <div className="p-4 text-gray-400 text-center">Loading...</div>;

  const consumables = overview?.consumables ?? [];
  const gems = (overview?.gems ?? []).filter((g) => g.quantity > 0);
  const shards = (overview?.shards ?? []).filter((s) => s.quantity > 0);
  const legendaryStones = (overview?.legendary_stones ?? []).filter((s) => s.quantity > 0);
  const emptySlots = overview ? Math.max(0, overview.capacity - overview.used) : 0;

  return (
    <div className="p-4 space-y-4 pb-8">
      <h2 className="text-xl font-bold font-game text-center text-gold-400">🎒 Inventory</h2>

      <div className="flex items-center justify-between bg-kingdom-purple rounded-xl border border-kingdom-mid px-4 py-3">
        <span className="text-gray-400 text-sm">
          Equipment slots {overview?.used ?? 0}/{overview?.capacity ?? "—"}
        </span>
        {overview && (
          <button
            onClick={() => runAction(() => inventoryApi.expand())}
            disabled={actionLoading || (balance?.diamonds ?? 0) < overview.slot_upgrade_cost_diamonds}
            className="text-xs text-kingdom-glow font-bold disabled:opacity-40"
          >
            +{overview.slot_upgrade_increment} slots (💎{overview.slot_upgrade_cost_diamonds})
          </button>
        )}
      </div>

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
      )}

      {success && (
        <div className="bg-emerald-900/30 border border-emerald-500 rounded-xl p-3 text-emerald-300 text-sm">
          {success}
        </div>
      )}

      {loading && <div className="text-center text-gray-400 py-8 animate-pulse">Loading...</div>}

      {overview && !loading && (
        <div className="grid grid-cols-4 gap-2">
          {consumables.map((item) => (
            <button
              key={`consumable-${item.id}`}
              type="button"
              disabled={actionLoading}
              onClick={() => {
                setError(null);
                setSuccess(null);
                setSelectedGem(null);
                setSelectedConsumable(item);
              }}
              className={clsx(
                "aspect-square rounded-xl border border-kingdom-mid bg-kingdom-dark flex flex-col items-center justify-center p-1 relative hover:border-kingdom-glow active:scale-95",
                actionLoading && "opacity-60",
              )}
            >
              <img
                src={getConsumableIconPath(item.item_id)}
                alt={item.name}
                className="w-10 h-10 object-contain bg-white rounded-md"
              />
              <AdminAssetPath
                path={getConsumableIconPath(item.item_id)}
                className="text-center w-full px-0.5 line-clamp-2"
              />
              <span className="text-[9px] text-kingdom-glow text-center leading-tight line-clamp-2 mt-0.5 px-0.5">
                {item.name}
              </span>
              {item.quantity > 1 && (
                <span className="absolute top-1 right-1 min-w-[1.1rem] h-[1.1rem] px-0.5 rounded-full bg-kingdom-accent text-white text-[9px] font-bold leading-[1.1rem] text-center">
                  {item.quantity}
                </span>
              )}
            </button>
          ))}

          {shards.map((stack) => {
            const iconSrc = getShardIconPath(stack.level);
            return (
            <div
              key={`shard-${stack.level}`}
              className="aspect-square rounded-xl border border-purple-500/40 bg-kingdom-dark flex flex-col items-center justify-center p-1 relative"
              title={`Armor shard Lv.${stack.level} — craft at the Blacksmith`}
            >
              <AssetIcon src={iconSrc} fallback={SHARD_ICON_FALLBACK} alt={`Shard Lv.${stack.level}`} />
              <AdminAssetPath path={iconSrc} className="text-center w-full px-0.5 line-clamp-2" />
              <span className="text-[9px] text-purple-300 text-center leading-tight line-clamp-2 mt-0.5 px-0.5">
                Shard Lv.{stack.level}
              </span>
              <span className="absolute top-1 right-1 min-w-[1.1rem] h-[1.1rem] px-0.5 rounded-full bg-kingdom-accent text-white text-[9px] font-bold leading-[1.1rem] text-center">
                {stack.quantity}
              </span>
            </div>
            );
          })}

          {legendaryStones.map((stack) => {
            const iconSrc = getLegendaryStoneIconPath(stack.level);
            return (
            <div
              key={`stone-${stack.level}`}
              className="aspect-square rounded-xl border border-gold-500/40 bg-kingdom-dark flex flex-col items-center justify-center p-1 relative"
              title={`Legendary stone Lv.${stack.level} — craft at the Blacksmith`}
            >
              <AssetIcon src={iconSrc} fallback={LEGENDARY_STONE_ICON_FALLBACK} alt={`Stone Lv.${stack.level}`} />
              <AdminAssetPath path={iconSrc} className="text-center w-full px-0.5 line-clamp-2" />
              <span className="text-[9px] text-gold-300 text-center leading-tight line-clamp-2 mt-0.5 px-0.5">
                Stone Lv.{stack.level}
              </span>
              <span className="absolute top-1 right-1 min-w-[1.1rem] h-[1.1rem] px-0.5 rounded-full bg-kingdom-accent text-white text-[9px] font-bold leading-[1.1rem] text-center">
                {stack.quantity}
              </span>
            </div>
            );
          })}

          {gems.map((gem) => {
            const iconSrc = getGemIconPath(gem.gem_type, gem.level);
            return (
            <button
              key={`gem-${gem.gem_type}-${gem.level}`}
              type="button"
              disabled={actionLoading}
              onClick={() => {
                setError(null);
                setSuccess(null);
                setSelectedConsumable(null);
                setSelected(null);
                setSelectedGem(gem);
              }}
              className={clsx(
                "aspect-square rounded-xl border-2 bg-kingdom-dark flex flex-col items-center justify-center p-1 relative hover:border-kingdom-glow active:scale-95",
                gemTypeSurface(gem.gem_type, hero.hero_class),
                actionLoading && "opacity-60",
              )}
            >
              <AssetIcon src={iconSrc} fallback={GEM_ICON_FALLBACK} alt={`${gem.gem_type} Lv.${gem.level}`} />
              <AdminAssetPath path={iconSrc} className="text-center w-full px-0.5 line-clamp-2" />
              <span
                className={clsx(
                  "text-[9px] text-center leading-tight line-clamp-2 mt-0.5 px-0.5 font-semibold",
                  gemTypeColor(gem.gem_type, hero.hero_class),
                )}
              >
                {formatGemLabel(gem.gem_type, hero.hero_class)} Lv.{gem.level}
              </span>
              {gem.quantity > 1 && (
                <span className="absolute top-1 right-1 min-w-[1.1rem] h-[1.1rem] px-0.5 rounded-full bg-kingdom-accent text-white text-[9px] font-bold leading-[1.1rem] text-center">
                  {gem.quantity}
                </span>
              )}
            </button>
            );
          })}

          {overview.items.map((item) => {
            const iconSrc = getItemIconPath(
              item.template.hero_class,
              item.template.slot,
              item.template.icon,
              item.template.item_level,
            );
            return (
            <button
              key={item.id}
              onClick={() => {
                setSelectedGem(null);
                setSelected(item);
              }}
              className={clsx(
                "aspect-square rounded-xl border-2 bg-kingdom-dark flex flex-col items-center justify-center p-1",
                RARITY_COLOR[item.template.rarity] ?? RARITY_COLOR.JUNK,
                item.is_equipped && "ring-2 ring-kingdom-glow",
              )}
            >
              <img
                src={iconSrc}
                alt={item.template.name}
                className="w-10 h-10 object-contain"
              />
              <AdminAssetPath path={iconSrc} className="text-center w-full px-0.5 line-clamp-2" />
              <span
                className={clsx(
                  "text-[9px] text-center leading-tight line-clamp-2 mt-0.5 px-0.5 font-semibold w-full",
                  (RARITY_COLOR[item.template.rarity] ?? RARITY_COLOR.JUNK).split(" ")[0],
                )}
              >
                {item.template.name}
              </span>
              <span className="text-[9px] text-gray-300 text-center leading-tight px-0.5">
                Lv.{item.template.item_level}
                {item.enhance_level > 0 ? ` +${item.enhance_level}` : ""}
              </span>
            </button>
            );
          })}

          {Array.from({ length: emptySlots }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className="aspect-square rounded-xl border border-dashed border-gray-700 bg-kingdom-dark/40"
            />
          ))}
        </div>
      )}

      {overview && !loading && consumables.length === 0 && gems.length === 0 && shards.length === 0 && legendaryStones.length === 0 && overview.items.length === 0 && (
        <p className="text-center text-gray-500 text-sm">Your bag is empty.</p>
      )}

      {overview && !loading && (consumables.length > 0 || gems.length > 0 || shards.length > 0 || legendaryStones.length > 0) && (
        <p className="text-[10px] text-gray-500 text-center">
          Tap a consumable or gem to view details. Shards and stones are used at the Blacksmith.
        </p>
      )}

      {selectedConsumable && (
        <ConsumableDetailModal
          item={selectedConsumable}
          onClose={() => setSelectedConsumable(null)}
          actionLoading={actionLoading}
          onUse={
            USABLE_STASH_ITEMS.has(selectedConsumable.item_id)
              ? (qty) => void useConsumable(selectedConsumable, qty)
              : undefined
          }
          onOpenHero={
            selectedConsumable.item_id === "rename_scroll"
              ? () => {
                  setSelectedConsumable(null);
                  setScreen("hero");
                }
              : undefined
          }
        />
      )}

      {selectedGem && (
        <GemDetailModal
          gem={selectedGem}
          heroClass={hero.hero_class}
          sellGoldPerUnit={
            blacksmithConfig?.gem_craft?.sell_gold_by_level?.[String(selectedGem.level)]
              ? Number(blacksmithConfig.gem_craft.sell_gold_by_level[String(selectedGem.level)])
              : undefined
          }
          actionLoading={actionLoading}
          onClose={() => setSelectedGem(null)}
          onSell={async (qty) => {
            setActionLoading(true);
            try {
              await sellGem(selectedGem.gem_type, selectedGem.level, qty);
              await load();
              await refreshBalance();
              setSelectedGem(null);
            } finally {
              setActionLoading(false);
            }
          }}
          onGoToBlacksmith={
            selectedGem.gem_type !== "LUCK"
              ? () => {
                  setSelectedGem(null);
                  setBlacksmithTab("gems");
                  setScreen("blacksmith");
                }
              : undefined
          }
        />
      )}

      {selected && (
        <ItemDetailModal
          item={selected}
          heroClass={hero.hero_class}
          gems={overview?.gems ?? []}
          socketUnlockCost={overview?.socket_unlock_cost_diamonds ?? 100}
          diamonds={balance?.diamonds ?? 0}
          onClose={() => setSelected(null)}
          actionLoading={actionLoading}
          onEquip={
            !selected.is_equipped
              ? () => runAction(() => inventoryApi.equip(selected.id))
              : undefined
          }
          onUnequip={
            selected.is_equipped
              ? () => runAction(() => inventoryApi.unequip(selected.template.slot))
              : undefined
          }
          onSell={
            !selected.is_equipped
              ? () => runAction(() => inventoryApi.sell(selected.id))
              : undefined
          }
          onUpgrade={
            selected.max_enhance > 0
              ? (luckLevel) => {
                  if (selected.enhance_level >= selected.max_enhance) return;
                  runUpgrade(selected.id, luckLevel, true);
                }
              : undefined
          }
          luckBonusPerLevel={blacksmithConfig?.gem_craft?.luck_bonus_per_level ?? 0.1}
          onUnlockSocket={
            selected.sockets_unlocked < selected.template.max_sockets
              ? () => runAction(() => inventoryApi.unlockSocket(selected.id), true)
              : undefined
          }
          onSocketGem={(socketIndex, gemType, gemLevel) =>
            runAction(
              () => inventoryApi.socketGem(selected.id, socketIndex, gemType, gemLevel),
              true,
            )
          }
          onUnsocketGem={(socketIndex) =>
            runAction(() => inventoryApi.unsocketGem(selected.id, socketIndex), true)
          }
        />
      )}
    </div>
  );
}
